/*
 * File: deliveryAddressStateFilter.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 30 Dec 2021 12:17:03 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Select, Space } from 'antd';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setFilterState } from 'redux/salesOrder/ordersSlice';
import { useGetWorldsQuery } from 'redux/services/useChuckieSueService';
import { useDebouncedCallback } from 'use-debounce';

const styles: InlineStylesModel = {
  labelUnfocused: {
    position: 'absolute',
    color: 'rgba(0,0,0,0.50)',
    top: 5,
    left: 10,
    zIndex: 2,
    transition: 'all 0.3s ease',
    pointerEvents: 'none'
  },
  labelFocused: {
    backgroundColor: 'white',
    position: 'absolute',
    paddingLeft: 5,
    paddingRight: 5,
    zIndex: 2,
    top: -15,
    left: 8,
    transition: 'all 0.3s ease',
    borderRadius: 5,
    pointerEvents: 'none'
  }
};

/* ****************** Interfaces ****************** */
interface Props {
  isDisabled?: boolean;
}

export const DeliveryAddressStateFilter = ({ isDisabled }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { data, isLoading } = useGetWorldsQuery();

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);

  const [isLabelTop, _setIsLabelTop] = useState(false);

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  const handleInputChange = useDebouncedCallback((option: string) => {
    _setInputValue(option);
    dispatch(setFilterState(option));
  }, 250);

  const handleClear = (): void => {
    _setInputValue(undefined);
    dispatch(setFilterState(undefined));
  };

  const handleBlur = (): void => {
    if (inputValue === undefined) {
      _setIsLabelTop(false);
    }
  };

  useEffect(() => {
    if (inputValue === undefined) {
      _setIsLabelTop(false);
    } else {
      _setIsLabelTop(true);
    }
  }, [inputValue, dispatch]);

  useEffect(() => {
    return (): void => {
      dispatch(setFilterState(undefined));
    };
  }, [dispatch]);

  const stateOptions = data?.data[0].countries.find((country) => country.alpha2Code === 'US')?.subdivisions.map((subdivision) => ({ label: subdivision.name, value: subdivision.localCode }));

  /* ****************** Render ****************** */
  return (
    <Space direction="vertical" size="small" style={{ position: 'relative', gap: 0, rowGap: 0, columnGap: 0 }}>
      {/* <FancyInput onChange={handleInputChange} disabled={isDisabled} placeholder="State" width={150} /> */}
      <Select
        disabled={isDisabled}
        id="custom-ant-select-input"
        style={{ width: 150, minWidth: 125 }}
        loading={isLoading}
        onSelect={(option: string): void => handleInputChange(option)}
        showSearch
        allowClear
        onClear={handleClear}
        onBlur={handleBlur}
        options={stateOptions}
        onFocus={(): void => _setIsLabelTop(true)}
        filterOption={(input, option): boolean => {
          if (option?.label) {
            return (option?.label as unknown as string).toLowerCase().includes(input.toLowerCase());
          } else {
            return false;
          }
        }}
      />
      <label style={isLabelTop ? styles.labelFocused : styles.labelUnfocused}>
        <span>State</span>
      </label>
    </Space>
  );
};
